import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Container,
  Backdrop,
  CircularProgress,
  Typography,
  Toolbar,
  Stack,
  Button,
  Paper,
  Grid,
  Link,
  Breadcrumbs,
  Divider,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';

import DeleteIcon from '@mui/icons-material/Delete';

import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useUser } from '@/providers';

import toNiceTimestamp from '../../util/toNiceTimestamp';
import { clearErrorMessage } from '../../actions/errorMessage';
import ErrorSnackBar from '../shared/ErrorSnackBar';

import { deleteProject, fetchProjects, leaveProject } from '@/actions/projects';

export default function ProjectDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const api_error = useSelector((state) => state.errorMessage);
  const projects = useSelector((state) => state.projects);
  const user = useUser();
  const my_projects = projects.filter(
    (project) => project.id !== user.id && project.owner,
  );
  const other_projects = projects.filter(
    (project) => project.id !== user.id && !project.owner,
  );
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });
  // const [transitioningProjects, setTransitioningProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return () => {
      dispatch(clearErrorMessage());
    };
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      if (projects === null) {
        dispatch(fetchProjects());
        return;
      }
      setLoading(false);
    })();
  }, [dispatch, projects]);

  const removeProject = useCallback(
    async (id) => {
      // setTransitioningProjects((prevTransitioningProjects) => [
      //   ...prevTransitioningProjects,
      //   id,
      // ]);
      await dispatch(deleteProject(id, user));
      // setTransitioningProjects((prevTransitioningProjects) =>
      //   prevTransitioningProjects.filter(
      //     (transitioning_id) => transitioning_id !== id
      //   )
      // );
    },
    [dispatch, user],
  );

  const deleteSelectedProjects = useCallback(async () => {
    let ids = [];
    if (rowSelectionModel.type === 'include') {
      ids = Array.from(rowSelectionModel.ids);
    } else {
      // exclude mode: all rows except those in ids
      const excludedIds = Array.from(rowSelectionModel.ids);
      ids = my_projects
        .map((project) => project.id)
        .filter((id) => !excludedIds.includes(id));
    }
    setRowSelectionModel({ type: 'include', ids: new Set() });
    // setTransitioningProjects((prevTransitioningProjects) => [
    //   ...prevTransitioningProjects,
    //   ...ids,
    // ]);
    for (let i = 0; i < ids.length; i++) {
      // parallel actions put a lot of stress on the API
      await dispatch(deleteProject(ids[i], user));
    }
    // setTransitioningProjects((prevTransitioningProjects) =>
    //   prevTransitioningProjects.filter((id) => !ids.includes(id))
    // );
  }, [dispatch, rowSelectionModel, my_projects]);

  const renderName = useCallback((params) => {
    return (
      <Link component={RouterLink} to={`/project/details/${params.id}`}>
        {params.row.name}
      </Link>
    );
  }, []);

  const renderCreatedDate = useCallback((params) => {
    return toNiceTimestamp(params.row.createdAt, true);
  }, []);

  const my_projects_columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        flex: 2,
        renderCell: renderName,
      },

      {
        field: 'createdAt',
        headerName: 'Created',
        type: 'string',
        renderCell: renderCreatedDate,
        minWidth: 150,
        flex: 0.5,
      },

      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 80,
        getActions: (params) => {
          const actions = [];

          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label='Remove'
              onClick={() => removeProject(params.id)}
              showInMenu
            />,
          );
          return actions;
        },
      },
    ],
    [renderCreatedDate, renderName, removeProject],
  );

  const other_projects_columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        flex: 2,
      },

      {
        field: 'owner_name',
        headerName: 'Owner',
        type: 'string',
        flex: 0.5,
      },

      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 80,
        getActions: (params) => {
          const actions = [];

          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label='Leave'
              onClick={() => {
                if (user) {
                  dispatch(leaveProject(params.id, user));
                }
              }}
              showInMenu
            />,
          );
          return actions;
        },
      },
    ],
    [dispatch, user],
  );

  if (loading) {
    return (
      <Backdrop open={loading}>
        <CircularProgress />
      </Backdrop>
    );
  }

  return (
    <Container>
      <Breadcrumbs aria-label='breadcrumb'>
        <Link
          underline='hover'
          color='inherit'
          component={RouterLink}
          to={`/dashboard`}
        >
          Home
        </Link>
        <Typography color='text.primary'>Projects</Typography>
      </Breadcrumbs>
      <br />

      <Grid container spacing={1}>
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Paper sx={{ display: 'flex', width: '100%' }} elevation={1}>
            <Toolbar>
              <Stack spacing={2} direction='row' alignItems='center'>
                <Typography variant='h4'>My Projects</Typography>
                <Button color='brand' onClick={() => navigate(`/project/edit`)}>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <AddCircleOutlineIcon />
                    <Typography variant='button' display='block'>
                      Create
                    </Typography>
                  </Stack>
                </Button>

                <Button
                  onClick={() => {
                    const selectedIds =
                      rowSelectionModel.type === 'include'
                        ? Array.from(rowSelectionModel.ids)
                        : [];
                    if (selectedIds.length === 1) {
                      navigate(`/project/edit?id=${selectedIds[0]}`);
                    }
                  }}
                  disabled={
                    !(
                      rowSelectionModel.type === 'include' &&
                      rowSelectionModel.ids.size === 1
                    )
                  }
                >
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <EditIcon />
                    <Typography variant='button' display='block'>
                      Edit
                    </Typography>
                  </Stack>
                </Button>

                <Button
                  onClick={deleteSelectedProjects}
                  disabled={
                    !(
                      (rowSelectionModel.type === 'include' &&
                        rowSelectionModel.ids.size > 0) ||
                      (rowSelectionModel.type === 'exclude' &&
                        rowSelectionModel.ids.size < my_projects.length)
                    )
                  }
                >
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <DeleteIcon />
                    <Typography variant='button' display='block'>
                      Delete
                    </Typography>
                  </Stack>
                </Button>
              </Stack>
            </Toolbar>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', height: '100%' }}>
              <div style={{ flexGrow: 1 }}>
                <DataGrid
                  autoHeight
                  checkboxSelection
                  getRowId={(row) => row.id}
                  columns={my_projects_columns}
                  rows={my_projects}
                  rowSelectionModel={rowSelectionModel}
                  onRowSelectionModelChange={(newSelectionModel) => {
                    setRowSelectionModel(newSelectionModel);
                  }}
                />
              </div>
            </div>
          </div>
        </Grid>
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Divider />
        </Grid>
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Typography variant='h5'>Projects Shared With Me</Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', height: '100%' }}>
              <div style={{ flexGrow: 1 }}>
                <DataGrid
                  autoHeight
                  getRowId={(row) => row.id}
                  columns={other_projects_columns}
                  rows={other_projects}
                />
              </div>
            </div>
          </div>
        </Grid>
      </Grid>

      <ErrorSnackBar message={api_error} />
    </Container>
  );
}
